import { useEffect, useState } from "react";
import "./Partner.css";
import Navbar from "./Navbar";
import Footer from "./Footer";
import {
  partnerRegister, partnerLogin, partnerForgot, partnerReset, partnerMe, listMyAds, createAd, updateAd,
  deleteAd, payAd, verifyPay, uploadFile, loadRazorpay,
  publicPlans, subscribePlan, subscribeVerify, updateProfile,
  getToken, setToken, clearToken,
} from "./partnerApi";

const BIZ = [
  { v: "hotel", l: "🏨 Hotel" }, { v: "cottage", l: "🏡 Cottage" }, { v: "vehicle", l: "🚗 Vehicle" },
  { v: "homestay", l: "🏠 Homestay" }, { v: "resort", l: "🌴 Resort" }, { v: "other", l: "📦 Other" },
];
const UNITS = ["per night", "per day", "per person", "per trip", "onwards"];
const emptyAd = () => ({
  businessType: "hotel", title: "", description: "", location: "", priceFrom: "",
  priceUnit: "per night", amenities: "", photos: [], videoUrl: "",
  contactPhone: "", contactWhatsapp: "",
});

export default function Partner() {
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("login"); // login | register | forgot | reset
  const [auth, setAuth] = useState({ name: "", email: "", password: "", phone: "", businessName: "", businessType: "hotel" });
  const [authErr, setAuthErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [resetPass, setResetPass] = useState({ a: "", b: "" });

  const [ads, setAds] = useState([]);
  const [planInfo, setPlanInfo] = useState(null);
  const [plans, setPlans] = useState([]);
  const [showPlans, setShowPlans] = useState(false);
  const [editing, setEditing] = useState(null); // ad form object (with id when editing)
  const [uploading, setUploading] = useState(false);
  const [profile, setProfile] = useState(null); // profile editor form
  const [pUploading, setPUploading] = useState(false);

  function openProfile() {
    setProfile({
      slug: partner.slug || "", businessName: partner.businessName || "", bio: partner.bio || "",
      coverPhoto: partner.coverPhoto || "", gallery: partner.gallery || [], videos: partner.videos || [],
      testimonials: partner.testimonials || [], socials: partner.socials || {},
      address: partner.address || "", mapLink: partner.mapLink || "",
      contactPerson: partner.contactPerson || "", contactEmail: partner.contactEmail || "",
      amenitiesText: (partner.amenities || []).join(", "),
    });
  }
  async function saveProfile() {
    setBusy(true);
    try {
      const payload = { ...profile, amenities: String(profile.amenitiesText || "").split(/[,\n]/).map((s) => s.trim()).filter(Boolean) };
      delete payload.amenitiesText;
      const me = await updateProfile(payload);
      setPartner(me); setProfile(null);
      alert(`✅ Profile saved!\nYour shareable page:\n${window.location.origin}/p/${me.slug}`);
    } catch (err) { alert(err.message); }
    finally { setBusy(false); }
  }
  async function onProfileUpload(e, kind) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPUploading(true);
    try {
      if (kind === "cover") { const url = await uploadFile("profile", files[0]); setProfile((p) => ({ ...p, coverPhoto: url })); }
      else { const urls = []; for (const f of files) urls.push(await uploadFile("profile", f)); setProfile((p) => ({ ...p, [kind === "video" ? "videos" : "gallery"]: [...(p[kind === "video" ? "videos" : "gallery"] || []), ...urls] })); }
    } catch (err) { alert(err.message); }
    finally { setPUploading(false); e.target.value = ""; }
  }

  useEffect(() => {
    publicPlans().then(setPlans).catch(() => {});
    // Password-reset link: /partner?reset=<token>
    const rt = new URLSearchParams(window.location.search).get("reset");
    if (rt) { setResetToken(rt); setMode("reset"); setLoading(false); return; }
    if (!getToken()) { setLoading(false); return; }
    partnerMe().then((p) => { setPartner(p); return refreshAds(); }).catch(() => clearToken()).finally(() => setLoading(false));
  }, []);

  const refreshAds = () => listMyAds().then((d) => { setAds(d.ads || []); setPlanInfo(d); }).catch(() => {});

  async function subscribe(plan) {
    setBusy(true);
    try {
      await loadRazorpay();
      const o = await subscribePlan(plan.id);
      const rzp = new window.Razorpay({
        key: o.keyId,
        subscription_id: o.subscriptionId, // recurring monthly auto-debit
        name: "OotyGo Partner Plans",
        description: `${plan.name} — 1st month FREE, then ₹${plan.price}/month auto-debit`,
        prefill: { name: partner.name, email: partner.email, contact: partner.phone },
        theme: { color: "#1a5c3a" },
        handler: async (resp) => {
          try { await subscribeVerify({ ...resp, planId: plan.id }); await refreshAds(); const me = await partnerMe(); setPartner(me); alert(`✅ ${plan.name} active — your 1st month is FREE! Auto-debit of ₹${plan.price}/month starts next month. ${plan.type === "bundle" ? `You can post up to ${plan.adLimit} ads.` : "You can now pay per ad."}`); }
          catch (err) { alert("Verification failed: " + err.message); }
        },
      });
      rzp.open();
    } catch (err) { alert(err.message); }
    finally { setBusy(false); }
  }

  async function submitAuth(e) {
    e.preventDefault();
    setAuthErr(""); setBusy(true);
    try {
      const fn = mode === "register" ? partnerRegister : partnerLogin;
      const { token, partner: p } = await fn(auth);
      setToken(token); setPartner(p); await refreshAds();
    } catch (err) { setAuthErr(err.message); }
    finally { setBusy(false); }
  }

  async function submitForgot(e) {
    e.preventDefault();
    setAuthErr(""); setBusy(true);
    try { await partnerForgot(auth.email); setForgotSent(true); }
    catch (err) { setAuthErr(err.message); }
    finally { setBusy(false); }
  }

  async function submitReset(e) {
    e.preventDefault();
    setAuthErr("");
    if (resetPass.a.length < 6) return setAuthErr("Password must be at least 6 characters");
    if (resetPass.a !== resetPass.b) return setAuthErr("Passwords do not match");
    setBusy(true);
    try {
      const { token, partner: p } = await partnerReset(resetToken, resetPass.a);
      setToken(token); setPartner(p); await refreshAds();
      window.history.replaceState({}, "", "/partner");
      setMode("login");
    } catch (err) { setAuthErr(err.message); }
    finally { setBusy(false); }
  }

  function backToLogin() { setMode("login"); setAuthErr(""); setForgotSent(false); window.history.replaceState({}, "", "/partner"); }

  function logout() { clearToken(); setPartner(null); setAds([]); }

  async function saveAd(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...editing,
        priceFrom: Number(editing.priceFrom) || 0,
        amenities: String(editing.amenities || "").split(",").map((x) => x.trim()).filter(Boolean),
      };
      if (editing.id) await updateAd(editing.id, payload);
      else await createAd(payload);
      setEditing(null); await refreshAds();
    } catch (err) { alert(err.message); }
    finally { setBusy(false); }
  }

  async function onUpload(e, kind) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    // need an ad id to upload — save a draft first if new
    let id = editing.id;
    setUploading(true);
    try {
      if (!id) {
        const created = await createAd({ ...editing, priceFrom: Number(editing.priceFrom) || 0, amenities: [] });
        id = created.id;
        setEditing((p) => ({ ...p, id }));
        await refreshAds();
      }
      if (kind === "video") {
        const url = await uploadFile(id, files[0]);
        setEditing((p) => ({ ...p, videoUrl: url }));
      } else {
        const urls = [];
        for (const f of files) urls.push(await uploadFile(id, f));
        setEditing((p) => ({ ...p, photos: [...(p.photos || []), ...urls] }));
      }
    } catch (err) { alert(err.message); }
    finally { setUploading(false); e.target.value = ""; }
  }

  async function pay(ad) {
    setBusy(true);
    try {
      await loadRazorpay();
      const o = await payAd(ad.id);
      const rzp = new window.Razorpay({
        key: o.keyId, amount: o.amount, currency: o.currency, order_id: o.orderId,
        name: "OotyGo Partner Ads", description: `Monthly listing — ${o.adTitle}`,
        prefill: { name: partner.name, email: partner.email, contact: partner.phone },
        theme: { color: "#1a5c3a" },
        handler: async (resp) => {
          try {
            await verifyPay({ ...resp, adId: ad.id });
            await refreshAds();
            alert("✅ Payment successful — your ad is now live for 30 days!");
          } catch (err) { alert("Payment verification failed: " + err.message); }
        },
      });
      rzp.open();
    } catch (err) { alert(err.message); }
    finally { setBusy(false); }
  }

  async function removeAd(ad) {
    if (!window.confirm(`Delete "${ad.title}"?`)) return;
    await deleteAd(ad.id); refreshAds();
  }

  if (loading) return <div className="pt-loading">Loading…</div>;

  return (
    <>
      <Navbar />
      <div className="pt-page">
        {!partner ? (
          /* ───── Auth ───── */
          <div className="pt-auth">
            <div className="pt-auth-card">
              {mode === "reset" ? (
                /* ── Set new password (from email link) ── */
                <>
                  <h1>Set a new password 🔐</h1>
                  <p className="pt-auth-sub">Choose a new password for your OotyGo Partner account.</p>
                  <form onSubmit={submitReset}>
                    <input type="password" placeholder="New password" value={resetPass.a} onChange={(e) => setResetPass({ ...resetPass, a: e.target.value })} required />
                    <input type="password" placeholder="Confirm new password" value={resetPass.b} onChange={(e) => setResetPass({ ...resetPass, b: e.target.value })} required />
                    {authErr && <p className="pt-err">{authErr}</p>}
                    <button className="pt-btn" disabled={busy}>{busy ? "Please wait…" : "Update password"}</button>
                  </form>
                  <p className="pt-switch"><button onClick={backToLogin}>← Back to login</button></p>
                </>
              ) : mode === "forgot" ? (
                /* ── Request reset link ── */
                <>
                  <h1>Reset your password</h1>
                  {forgotSent ? (
                    <p className="pt-auth-sub">✅ If an account exists for <strong>{auth.email}</strong>, we've emailed a password reset link. Please check your inbox (and spam folder). The link expires in 1 hour.</p>
                  ) : (
                    <>
                      <p className="pt-auth-sub">Enter your account email and we'll send you a link to reset your password.</p>
                      <form onSubmit={submitForgot}>
                        <input type="email" placeholder="Email" value={auth.email} onChange={(e) => setAuth({ ...auth, email: e.target.value })} required />
                        {authErr && <p className="pt-err">{authErr}</p>}
                        <button className="pt-btn" disabled={busy}>{busy ? "Sending…" : "Send reset link"}</button>
                      </form>
                    </>
                  )}
                  <p className="pt-switch"><button onClick={backToLogin}>← Back to login</button></p>
                </>
              ) : (
                /* ── Login / Register ── */
                <>
                  <h1>{mode === "register" ? "Become an OotyGo Partner" : "Partner Login"}</h1>
                  <p className="pt-auth-sub">List your hotel, cottage or vehicle and reach thousands of Ooty travellers.</p>
                  <form onSubmit={submitAuth}>
                    {mode === "register" && (
                      <>
                        <input placeholder="Your name" value={auth.name} onChange={(e) => setAuth({ ...auth, name: e.target.value })} required />
                        <input placeholder="Business name" value={auth.businessName} onChange={(e) => setAuth({ ...auth, businessName: e.target.value })} />
                        <input placeholder="Phone" value={auth.phone} onChange={(e) => setAuth({ ...auth, phone: e.target.value })} />
                        <select value={auth.businessType} onChange={(e) => setAuth({ ...auth, businessType: e.target.value })}>
                          {BIZ.map((b) => <option key={b.v} value={b.v}>{b.l}</option>)}
                        </select>
                      </>
                    )}
                    <input type="email" placeholder="Email" value={auth.email} onChange={(e) => setAuth({ ...auth, email: e.target.value })} required />
                    <input type="password" placeholder="Password" value={auth.password} onChange={(e) => setAuth({ ...auth, password: e.target.value })} required />
                    {authErr && <p className="pt-err">{authErr}</p>}
                    <button className="pt-btn" disabled={busy}>{busy ? "Please wait…" : mode === "register" ? "Create account" : "Log in"}</button>
                  </form>
                  {mode === "login" && (
                    <p className="pt-forgot"><button type="button" onClick={() => { setMode("forgot"); setAuthErr(""); setForgotSent(false); }}>Forgot password?</button></p>
                  )}
                  <p className="pt-switch">
                    {mode === "register" ? "Already a partner? " : "New partner? "}
                    <button onClick={() => { setMode(mode === "register" ? "login" : "register"); setAuthErr(""); }}>
                      {mode === "register" ? "Log in" : "Sign up free"}
                    </button>
                  </p>
                </>
              )}
            </div>
          </div>
        ) : (
          /* ───── Dashboard ───── */
          <div className="pt-dash">
            <div className="pt-dash-head">
              <div>
                <h1>Hi {partner.name} 👋</h1>
                <p>{partner.businessName || "Partner"} · {partner.email}</p>
              </div>
              <div className="pt-dash-actions">
                <button className="pt-btn" onClick={() => setEditing(emptyAd())}>+ New Listing</button>
                <button className="pt-btn ghost" onClick={openProfile}>🌐 My Profile Page</button>
                <button className="pt-btn ghost" onClick={logout}>Log out</button>
              </div>
            </div>

            {/* Shareable profile link */}
            {partner.slug && (
              <div className="pt-profile-link">
                🔗 Your public page:&nbsp;
                <a href={`/p/${partner.slug}`} target="_blank" rel="noopener noreferrer">{window.location.origin}/p/{partner.slug}</a>
                <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/p/${partner.slug}`); alert("Link copied!"); }}>Copy</button>
              </div>
            )}

            {/* Current plan + plan picker (picker hidden once a plan is active) */}
            <div className="pt-plans">
              <div className="pt-plan-current">
                <div>
                  <span className="pt-plan-label">Your plan</span>
                  <h3>{planInfo?.plan?.name || "Ads-wise"}</h3>
                  <p>
                    {planInfo?.plan?.type === "bundle"
                      ? <>📢 {planInfo.adsUsed}/{planInfo.plan.adLimit} ads used{planInfo.bundleActive && planInfo.planValidUntil ? ` · valid until ${new Date(planInfo.planValidUntil).toLocaleDateString()}` : " · not active"}</>
                      : "Pay ₹499/month per ad you list."}
                  </p>
                </div>
                {planInfo?.bundleActive && (
                  <button className="pt-plan-change" onClick={() => setShowPlans((s) => !s)}>
                    {showPlans ? "Hide plans" : "Change / upgrade plan"}
                  </button>
                )}
              </div>
              {(!planInfo?.bundleActive || showPlans) && (
              <div className="pt-plan-cards">
                {plans.map((pl) => {
                  const isCurrent = planInfo?.plan?.id === pl.id && (pl.type !== "bundle" || planInfo?.bundleActive);
                  return (
                    <div className={`pt-plan-card ${pl.id === "pro" ? "best" : ""} ${isCurrent ? "current" : ""}`} key={pl.id}>
                      {pl.id === "pro" && <span className="pt-plan-tag">Best value</span>}
                      <h4>{pl.name}</h4>
                      <p className="pt-plan-price">₹{pl.price.toLocaleString("en-IN")}<span>/mo</span></p>
                      <p className="pt-plan-limit">{pl.type === "bundle" ? `Up to ${pl.adLimit} ads` : "Pay per ad"}</p>
                      <p className="pt-plan-desc">{pl.desc}</p>
                      <p className="pt-plan-trial">🎁 1st month FREE · auto-renews after</p>
                      <button className="pt-btn sm" disabled={busy} onClick={() => subscribe(pl)}>
                        {isCurrent ? "Renew" : "Start free month"}
                      </button>
                    </div>
                  );
                })}
              </div>
              )}
            </div>

            {ads.length === 0 && !editing && (
              <div className="pt-empty">You have no listings yet. Create your first ad to get featured on ootygo.com!</div>
            )}

            <div className="pt-ads">
              {ads.map((ad) => (
                <div className="pt-ad" key={ad.id}>
                  <div className="pt-ad-media">
                    {ad.photos?.[0] ? <img src={ad.photos[0]} alt={ad.title} /> : <div className="pt-ad-noimg">No photo</div>}
                    <span className={`pt-badge ${ad.live ? "live" : "paused"}`}>{ad.live ? "● Live" : ad.status === "draft" ? "Draft" : "Paused"}</span>
                  </div>
                  <div className="pt-ad-body">
                    <h3>{ad.title || "Untitled"}</h3>
                    <p className="pt-ad-loc">📍 {ad.location || "—"} · {BIZ.find((b) => b.v === ad.businessType)?.l}</p>
                    {ad.priceFrom > 0 && <p className="pt-ad-price">₹{ad.priceFrom.toLocaleString("en-IN")} <span>{ad.priceUnit}</span></p>}
                    {ad.validUntil && <p className="pt-ad-valid">{ad.live ? "Active until" : "Expired"}: {new Date(ad.validUntil).toLocaleDateString()}</p>}
                    <div className="pt-ad-btns">
                      {!ad.live && planInfo?.plan?.type !== "bundle" && <button className="pt-btn sm" onClick={() => pay(ad)} disabled={busy}>{ad.validUntil ? "Renew ₹499" : "Activate ₹499/mo"}</button>}
                      {!ad.live && planInfo?.plan?.type === "bundle" && <span className="pt-ad-cover">{planInfo?.bundleActive ? "Over plan limit — upgrade" : "Subscribe to a plan above"}</span>}
                      <button className="pt-btn sm ghost" onClick={() => setEditing({ ...ad, amenities: (ad.amenities || []).join(", ") })}>Edit</button>
                      <button className="pt-btn sm danger" onClick={() => removeAd(ad)}>Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ───── Ad editor ───── */}
        {editing && (
          <div className="pt-modal" onClick={(e) => e.target.classList.contains("pt-modal") && setEditing(null)}>
            <form className="pt-editor" onSubmit={saveAd}>
              <div className="pt-editor-head">
                <h2>{editing.id ? "Edit listing" : "New listing"}</h2>
                <button type="button" onClick={() => setEditing(null)}>×</button>
              </div>
              <div className="pt-editor-body">
                <label>Type</label>
                <select value={editing.businessType} onChange={(e) => setEditing({ ...editing, businessType: e.target.value })}>
                  {BIZ.map((b) => <option key={b.v} value={b.v}>{b.l}</option>)}
                </select>
                <label>Title *</label>
                <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="e.g. Misty Hill View Cottage" required />
                <label>Description</label>
                <textarea rows={4} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Describe your property / vehicle, what's special…" />
                <div className="pt-row">
                  <div><label>Location</label><input value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} placeholder="Coonoor, Ooty…" /></div>
                  <div><label>Price from (₹)</label><input type="number" value={editing.priceFrom} onChange={(e) => setEditing({ ...editing, priceFrom: e.target.value })} placeholder="0" /></div>
                  <div><label>Unit</label><select value={editing.priceUnit} onChange={(e) => setEditing({ ...editing, priceUnit: e.target.value })}>{UNITS.map((u) => <option key={u}>{u}</option>)}</select></div>
                </div>
                <label>Amenities / Features (comma separated)</label>
                <input value={editing.amenities} onChange={(e) => setEditing({ ...editing, amenities: e.target.value })} placeholder="Wifi, Parking, Hill view, Breakfast" />
                <div className="pt-row">
                  <div><label>Contact phone</label><input value={editing.contactPhone} onChange={(e) => setEditing({ ...editing, contactPhone: e.target.value })} placeholder="+91…" /></div>
                  <div><label>WhatsApp</label><input value={editing.contactWhatsapp} onChange={(e) => setEditing({ ...editing, contactWhatsapp: e.target.value })} placeholder="+91…" /></div>
                </div>

                <label>📷 Photo Gallery <span className="pt-photo-count">{(editing.photos || []).length}/12</span> {uploading && <span className="pt-up">uploading…</span>}</label>
                <label className="pt-upload-box">
                  <input type="file" accept="image/*" multiple hidden onChange={(e) => onUpload(e, "photo")} />
                  <span>📁 Click to upload photos — select multiple at once</span>
                </label>
                <div className="pt-thumbs">
                  {(editing.photos || []).map((url, i) => (
                    <div className={`pt-thumb${i === 0 ? " cover" : ""}`} key={i}>
                      <img src={url} alt="" />
                      {i === 0
                        ? <span className="pt-cover-badge">★ Cover</span>
                        : <button type="button" className="pt-set-cover" title="Set as cover photo" onClick={() => setEditing((p) => { const ph = [...p.photos]; const [m] = ph.splice(i, 1); ph.unshift(m); return { ...p, photos: ph }; })}>★</button>}
                      <button type="button" className="pt-thumb-del" onClick={() => setEditing((p) => ({ ...p, photos: p.photos.filter((_, j) => j !== i) }))}>×</button>
                    </div>
                  ))}
                </div>

                <label>🎬 Video (optional)</label>
                <input type="file" accept="video/*" onChange={(e) => onUpload(e, "video")} />
                {editing.videoUrl && <video src={editing.videoUrl} controls className="pt-vid" />}
              </div>
              <div className="pt-editor-foot">
                <button type="submit" className="pt-btn" disabled={busy || uploading}>{busy ? "Saving…" : "Save listing"}</button>
                <button type="button" className="pt-btn ghost" onClick={() => setEditing(null)}>Cancel</button>
              </div>
              <p className="pt-note">💡 Save your listing, then click <b>Activate ₹499/mo</b> on the card to pay &amp; go live. Listings auto-pause if the monthly payment isn't renewed.</p>
            </form>
          </div>
        )}

        {/* ───── My Profile editor ───── */}
        {profile && (
          <div className="pt-modal" onClick={(e) => e.target.classList.contains("pt-modal") && setProfile(null)}>
            <div className="pt-editor">
              <div className="pt-editor-head">
                <h2>🌐 My Profile Page</h2>
                <button type="button" onClick={() => setProfile(null)}>×</button>
              </div>
              <div className="pt-editor-body">
                <label>Profile handle (your shareable link)</label>
                <div className="pt-slug-row">
                  <span>{window.location.origin}/p/</span>
                  <input value={profile.slug} onChange={(e) => setProfile({ ...profile, slug: e.target.value })} placeholder="misty-hill-cottage" />
                </div>
                <label>Business name</label>
                <input value={profile.businessName} onChange={(e) => setProfile({ ...profile, businessName: e.target.value })} />
                <label>About / Bio</label>
                <textarea rows={3} value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} placeholder="Tell visitors about your property / service…" />

                <label>📍 Address</label>
                <input value={profile.address || ""} onChange={(e) => setProfile({ ...profile, address: e.target.value })} placeholder="Full address, Coonoor, The Nilgiris" />
                <label>🗺️ Google Maps link (optional)</label>
                <input value={profile.mapLink || ""} onChange={(e) => setProfile({ ...profile, mapLink: e.target.value })} placeholder="Paste your Google Maps share link" />
                <label>✨ Amenities (comma separated)</label>
                <input value={profile.amenitiesText || ""} onChange={(e) => setProfile({ ...profile, amenitiesText: e.target.value })} placeholder="Wifi, Parking, Hill view, Breakfast, Pet friendly" />
                <div className="pt-row2">
                  <div><label>👤 Contact person</label><input value={profile.contactPerson || ""} onChange={(e) => setProfile({ ...profile, contactPerson: e.target.value })} /></div>
                  <div><label>✉️ Contact email</label><input value={profile.contactEmail || ""} onChange={(e) => setProfile({ ...profile, contactEmail: e.target.value })} /></div>
                </div>

                <label>🖼️ Cover photo {pUploading && <span className="pt-up">uploading…</span>}</label>
                <label className="pt-upload-box"><input type="file" accept="image/*" hidden onChange={(e) => onProfileUpload(e, "cover")} /><span>📁 Upload cover image</span></label>
                {profile.coverPhoto && <img src={profile.coverPhoto} alt="" className="pt-cover-preview" />}

                <label>📷 Photo gallery</label>
                <label className="pt-upload-box"><input type="file" accept="image/*" multiple hidden onChange={(e) => onProfileUpload(e, "gallery")} /><span>📁 Upload photos (multiple)</span></label>
                <div className="pt-thumbs">
                  {(profile.gallery || []).map((url, i) => (
                    <div className="pt-thumb" key={i}><img src={url} alt="" /><button type="button" className="pt-thumb-del" onClick={() => setProfile((p) => ({ ...p, gallery: p.gallery.filter((_, j) => j !== i) }))}>×</button></div>
                  ))}
                </div>

                <label>🎬 Videos</label>
                <label className="pt-upload-box"><input type="file" accept="video/*" multiple hidden onChange={(e) => onProfileUpload(e, "video")} /><span>📁 Upload videos</span></label>
                <div className="pt-thumbs">
                  {(profile.videos || []).map((url, i) => (
                    <div className="pt-thumb vid" key={i}><video src={url} /><button type="button" className="pt-thumb-del" onClick={() => setProfile((p) => ({ ...p, videos: p.videos.filter((_, j) => j !== i) }))}>×</button></div>
                  ))}
                </div>

                <label>⭐ Reviews / Testimonials</label>
                {(profile.testimonials || []).map((t, i) => (
                  <div className="pt-testi" key={i}>
                    <div className="pt-testi-row">
                      <input placeholder="Customer name" value={t.name} onChange={(e) => setProfile((p) => ({ ...p, testimonials: p.testimonials.map((x, j) => j === i ? { ...x, name: e.target.value } : x) }))} />
                      <select value={t.rating || 5} onChange={(e) => setProfile((p) => ({ ...p, testimonials: p.testimonials.map((x, j) => j === i ? { ...x, rating: Number(e.target.value) } : x) }))}>
                        {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r}★</option>)}
                      </select>
                      <button type="button" onClick={() => setProfile((p) => ({ ...p, testimonials: p.testimonials.filter((_, j) => j !== i) }))}>×</button>
                    </div>
                    <textarea rows={2} placeholder="Review text" value={t.text} onChange={(e) => setProfile((p) => ({ ...p, testimonials: p.testimonials.map((x, j) => j === i ? { ...x, text: e.target.value } : x) }))} />
                  </div>
                ))}
                <button type="button" className="pt-add-testi" onClick={() => setProfile((p) => ({ ...p, testimonials: [...(p.testimonials || []), { name: "", text: "", rating: 5 }] }))}>+ Add a review</button>

                <label>🔗 Social links</label>
                <input placeholder="Instagram URL" value={profile.socials?.instagram || ""} onChange={(e) => setProfile((p) => ({ ...p, socials: { ...p.socials, instagram: e.target.value } }))} />
                <input placeholder="Facebook URL" value={profile.socials?.facebook || ""} onChange={(e) => setProfile((p) => ({ ...p, socials: { ...p.socials, facebook: e.target.value } }))} />
                <input placeholder="Website URL" value={profile.socials?.website || ""} onChange={(e) => setProfile((p) => ({ ...p, socials: { ...p.socials, website: e.target.value } }))} />
              </div>
              <div className="pt-editor-foot">
                <button className="pt-btn" disabled={busy || pUploading} onClick={saveProfile}>{busy ? "Saving…" : "Save profile"}</button>
                <button className="pt-btn ghost" onClick={() => setProfile(null)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
